import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import type { IDLSModel, IDLSSuspensionModel, IPenaltyRunsModel } from '@clsplus/cls-plus-data-models'
import {
  convertBallsTotalToOvers,
  convertOversToBallsTotal,
  getDlsValue,
  getOversRemaining,
} from '@clsplus/cricket-logic'
import { each, filter, find, isNil, sortBy } from 'lodash'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { ReactElement, ReactNode } from 'react'
import { useMemo, useRef, useState } from 'react'

import { db } from '../../data/dexie/Database'
import { useMst } from '../../data/stores/rootStore'
import { isCoreModeRetiredEnabled } from '../../helpers/coreModeRetired'
import MatchHelpers from '../../helpers/matchHelpers'
import S3PHelpers from '../../helpers/s3pHelpers'
import type { ISettingsModel } from '../../types/models'
import type { DlsProps } from '../../types/props'
import { Button } from '../Buttons/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import EditableField from '../EditableField/EditableField'
import type { TextFieldCallbackArgs } from '../TextField/TextField'
import TextField from '../TextField/TextField'
import { DlsSuspensions } from './DlsSuspensions'

type DlsTextRowProps = {
  label: string
  value: number | string
}

type DlsEditableOversRowProps = {
  label: string
  testId: string
  value: number
  onChange: (value: string) => void
}

type DlsSuspensionSectionProps = {
  canAddSuspension: boolean
  data: IDLSSuspensionModel[]
  game: DlsProps['game']
  inningsId: string
  inSuperOver: boolean
  onAddSuspension: () => void
}

type DlsTabPanelContainerProps = {
  children: ReactNode
  dataJson?: string
}

type LooseComponent = (props: Record<string, unknown> & { children?: ReactNode }) => ReactElement | null

const LooseButton = Button as unknown as LooseComponent
const LooseBox = Box as unknown as LooseComponent
const LooseCheckbox = Checkbox as unknown as LooseComponent
const LooseAlert = Alert as unknown as LooseComponent
const LooseAlertDialog = AlertDialog as unknown as LooseComponent
const LooseAlertDialogBody = AlertDialogBody as unknown as LooseComponent
const LooseAlertDialogContent = AlertDialogContent as unknown as LooseComponent
const LooseAlertDialogFooter = AlertDialogFooter as unknown as LooseComponent
const LooseAlertDialogHeader = AlertDialogHeader as unknown as LooseComponent
const LooseAlertDialogOverlay = AlertDialogOverlay as unknown as LooseComponent
const LooseAlertDescription = AlertDescription as unknown as LooseComponent
const LooseAlertIcon = AlertIcon as unknown as LooseComponent
const LooseDlsSuspensions = DlsSuspensions as unknown as LooseComponent
const LooseEditableField = EditableField as unknown as LooseComponent
const LooseFlex = Flex as unknown as LooseComponent
const LooseModal = Modal as unknown as LooseComponent
const LooseModalBody = ModalBody as unknown as LooseComponent
const LooseModalCloseButton = ModalCloseButton as unknown as LooseComponent
const LooseModalContent = ModalContent as unknown as LooseComponent
const LooseModalFooter = ModalFooter as unknown as LooseComponent
const LooseModalHeader = ModalHeader as unknown as LooseComponent
const LooseModalOverlay = ModalOverlay as unknown as LooseComponent
const LooseTab = Tab as unknown as LooseComponent
const LooseTabList = TabList as unknown as LooseComponent
const LooseTabPanels = TabPanels as unknown as LooseComponent
const LooseTabPanel = TabPanel as unknown as LooseComponent
const LooseTabs = Tabs as unknown as LooseComponent
const LooseTextField = TextField as unknown as LooseComponent
const LooseText = Text as unknown as LooseComponent

const DlsTextRow = ({ label, value }: DlsTextRowProps): ReactElement => {
  return (
    <LooseFlex h="40px" direction="row" align="center">
      <LooseFlex flex={1}>
        <LooseText fontWeight="bold">{label}</LooseText>
      </LooseFlex>
      <LooseFlex flex={1.75}>
        <LooseText>{value}</LooseText>
      </LooseFlex>
    </LooseFlex>
  )
}

const DlsEditableOversRow = ({ label, testId, value, onChange }: DlsEditableOversRowProps): ReactElement => {
  return (
    <LooseFlex h="40px" direction="row" align="center">
      <LooseFlex flex={1}>
        <LooseText fontWeight="bold">{label}</LooseText>
      </LooseFlex>
      <LooseFlex flex={1.75}>
        <LooseEditableField
          key="overs2"
          data-testid={testId}
          height="32px"
          width="100%"
          textAlign="left"
          value={value}
          onChange={onChange}
          pattern="^\d{0,4}(?:\.[0-5]{1})?$"
          errorMessage="Please enter numbers, or decimals up to 1 digit"
        />
      </LooseFlex>
    </LooseFlex>
  )
}

const DlsSuspensionSection = ({
  canAddSuspension,
  data,
  game,
  inningsId,
  inSuperOver,
  onAddSuspension,
}: DlsSuspensionSectionProps): ReactElement => {
  return (
    <LooseFlex direction="column">
      <LooseFlex h="40px" w="100%" direction="row" align="center" justify="space-between" mb="10px">
        <LooseText fontWeight="bold">Suspension Periods:</LooseText>
        {canAddSuspension && (
          <LooseButton
            onClick={onAddSuspension}
            h="30px"
            colorScheme="green"
            ml={3}
            isDisabled={inSuperOver}
            data-testid="addSuspensionButton"
          >
            Add Suspension
          </LooseButton>
        )}
      </LooseFlex>
      <LooseDlsSuspensions game={game} inningsId={inningsId} data={data} inSuperOver={inSuperOver} />
    </LooseFlex>
  )
}

const DlsTabPanelContainer = ({ children, dataJson }: DlsTabPanelContainerProps): ReactElement => {
  return (
    <LooseTabPanel padding="7px" marginTop="14px">
      {!isNil(dataJson) && <LooseText display="none">{dataJson}</LooseText>}
      {children}
    </LooseTabPanel>
  )
}

export const Dls = observer(({ game, inningsInOrder, dlsOpen, setDlsOpen, inSuperOver }: DlsProps) => {
  const { appSettings }: { appSettings: ISettingsModel } = useMst()
  const coreRetired = isCoreModeRetiredEnabled()
  const isActingAsCore =
    (appSettings.appMode === 'core' && !coreRetired) || (coreRetired && appSettings.appMode === 'advanced')
  const [powerPlayWarningWasShown, setPowerPlayWarningWasShown] = useState(game.matchDls?.active ? true : false)
  const [powerPlayWarningOpen, setPowerPlayWarningOpen] = useState(false)
  const [isChangingTargetScore, setIsChangingTargetScore] = useState(false)
  const [hasChangedTargetScore, setHasChangedTargetScore] = useState(false)
  const [hasChangedTargetOvers, setHasChangedTargetOvers] = useState(false)
  const nonStandardGame = !isNil(game.matchConfigs.ballsPerOver) && game.matchConfigs.ballsPerOver !== 6
  const standardGameWithManualDlsExisting =
    (!isNil(game.matchDls?.targetOvers) || !isNil(game.matchDls?.targetScore)) &&
    (game.matchDls?.matchDlsSuspensions?.length ?? 0) === 0
  const [isManual, setIsManual] = useState(nonStandardGame || standardGameWithManualDlsExisting)
  const [preManualDlsObject, setPreManualDlsObject] = useState<IDLSModel | null>(null)
  const cancelPowerPlayWarningRef = useRef(null)

  const matchSettings = appSettings.getMatchSettings(game.id)

  const closeDls = () => {
    setDlsOpen(false)
    if (game.matchDls && (hasChangedTargetScore || hasChangedTargetOvers) && isActingAsCore) {
      const inningsOrder = find(inningsInOrder, { isActiveInning: true })?.inningsMatchOrder
      if (inningsOrder !== undefined) {
        db.createS3PMessage(S3PHelpers.metadata(appSettings.appMode, game), S3PHelpers.dls(game, inningsOrder < 2))
      }
    }
    setHasChangedTargetScore(false)
    setHasChangedTargetOvers(false)
    if (game.matchDls?.active && game.matchConfigs.maxOvers && !powerPlayWarningWasShown) {
      // for limited overs games, show warning about power play activation now that DLS is turned ON
      setPowerPlayWarningWasShown(true)
      setPowerPlayWarningOpen(true)
    }
  }
  const addSuspension = (inningsId: string, inningsNum: number) => {
    const maxOvers = inningsNum === 1 ? totalOvers1() : game.matchDls?.targetOvers ?? startingOvers2()
    game.createDlsSuspension(inningsId, inningsNum, maxOvers)
    if (inningsNum === 1) totalOvers1()
    calculateDlsTargetOvers()
  }
  const updateTargetOversManual = (value: number) => {
    if (isNaN(value)) return
    if (!game.matchDls) game.createDlsObject()
    game.updateDlsTargetOvers(Number(value))
    if (nonStandardGame || standardGameWithManualDlsExisting) setHasChangedTargetOvers(true)
  }
  const updateTargetScoreManual = ({ value }: TextFieldCallbackArgs) => {
    setIsChangingTargetScore(true)
    if (isNaN(Number(value)) || value === '') return
    if (!game.matchDls) game.createDlsObject()
    game.updateDlsTargetScore(Number(value))
    game.setDescription(MatchHelpers.gameDescriptionString(game))
    if (nonStandardGame || standardGameWithManualDlsExisting) setHasChangedTargetScore(true)
    setIsChangingTargetScore(false)
  }
  const clearTargetOversScoreManual = () => {
    setIsChangingTargetScore(false)
    if (preManualDlsObject) {
      // if DLS object existed before manual was switched on, rehydrate it now that we are resetting
      game.createDlsObject(preManualDlsObject)
      setPreManualDlsObject(null)
    } else {
      // otherwise, just clear the entire DLS object
      game.clearDlsObject()
    }
  }
  const updateManualOverride = () => {
    if (!isManual) {
      // turn ON manual
      const startingTargetOvers = startingOvers2() || game.matchConfigs?.maxOvers || 20
      if (game.matchDls) {
        // save copy of existing DLS object, then clear game.matchDls
        setPreManualDlsObject(toJS(game.matchDls))
        game.clearDlsObject()
      }
      updateTargetOversManual(startingTargetOvers)
      const startingTargetScore = !isNil(game.getInningByNum(1))
        ? (game.getInningByNum(1)?.progressiveScores.runs || 0) + 1
        : null
      if (startingTargetScore) updateTargetScoreManual({ value: `${startingTargetScore}` })
      setHasChangedTargetOvers(true)
      setHasChangedTargetScore(true)
    } else if (isManual && preManualDlsObject) {
      // turn OFF manual - rehydrate copy of DLS object
      clearTargetOversScoreManual()
    }
    setIsManual(!isManual)
  }

  // dls data points
  const parseOversValue = (value: number | string | null | undefined) => {
    const parsedValue = Number(value)
    return Number.isNaN(parsedValue) ? 0 : parsedValue
  }

  const totalOvers1 = () => {
    if (!inningsInOrder) return null
    if (isManual) {
      const batTeam1TotalOvers = game.getInningByNum(1)?.progressiveScores.oversBowled
      return !isNil(batTeam1TotalOvers) ? parseOversValue(batTeam1TotalOvers) : game.matchConfigs?.maxOvers || 0
    }
    let baseline = game.matchConfigs?.maxOvers || 0
    // get latest suspension period in the innings
    const suspensions = sortBy(
      filter(game.matchDls?.matchDlsSuspensions, { inningsId: inningsInOrder[0].value }),
      (s: IDLSSuspensionModel) => Number(s.oversAt)
    )
    if (suspensions && suspensions.length > 0) {
      // work out the total overs by when the final suspension period started, and how many overs left after it finished
      baseline = convertBallsTotalToOvers({
        balls:
          convertOversToBallsTotal({ overs: Number(suspensions[suspensions.length - 1].oversAt) }) +
          convertOversToBallsTotal({ overs: Number(suspensions[suspensions.length - 1].oversRemaining) }),
      })
    }
    return baseline
  }
  const startingOvers2 = () => {
    if (!inningsInOrder) return null
    let baseline = totalOvers1()
    each(game.matchDls?.matchDlsSuspensions, suspension => {
      if (
        baseline &&
        inningsInOrder.length > 1 &&
        suspension.inningsId === inningsInOrder[1].value &&
        Number(suspension.oversAt) === 0
      ) {
        baseline = Number(suspension.oversRemaining)
      }
    })
    return parseOversValue(baseline)
  }
  const penaltyRuns2 = () => {
    if (!inningsInOrder || inningsInOrder.length < 2) return 0
    let penaltyRunsTeam1InTeam2Innings = 0
    game.penaltyRuns.forEach((pr: IPenaltyRunsModel) => {
      if (!pr.timestamp || !inningsInOrder[1].startTime) return
      if (pr.inningsNum === 1 && pr.timestamp >= inningsInOrder[1].startTime) {
        // only include penalty runs given to 1st batting team during 2nd batting team's innings
        penaltyRunsTeam1InTeam2Innings += pr.runs
      }
    })
    return penaltyRunsTeam1InTeam2Innings
  }

  // dls calculations
  const calculateDlsTargetOvers = () => {
    if (!inningsInOrder) return null
    if (!game.matchDls?.active) return null
    let baseline = totalOvers1() || game.matchConfigs?.maxOvers || 0
    // get latest suspension period in the innings
    const suspensions = sortBy(
      filter(game.matchDls?.matchDlsSuspensions, {
        inningsId:
          find(inningsInOrder, { isActiveInning: true })?.value ||
          inningsInOrder[inningsInOrder.length > 1 ? 1 : 0].value,
      }),
      (s: IDLSSuspensionModel) => Number(s.oversAt)
    )
    if (suspensions && suspensions.length > 0) {
      // work out the total overs by when the final suspension period started, and how many overs left after it finished
      baseline = convertBallsTotalToOvers({
        balls:
          convertOversToBallsTotal({ overs: Number(suspensions[suspensions.length - 1].oversAt) }) +
          convertOversToBallsTotal({ overs: Number(suspensions[suspensions.length - 1].oversRemaining) }),
      })
    }
    const oversValue = parseOversValue(baseline)
    if (oversValue !== (game.matchDls?.targetOvers || 0) && !hasChangedTargetOvers) setHasChangedTargetOvers(true)
    game.updateDlsTargetOvers(oversValue)
    if (inningsInOrder.length === 1) {
      calculateDlsValue(false, true, true)
    }
  }
  const calculateDlsValue = (par: boolean, target: boolean, ignoreReturn?: boolean) => {
    if (!inningsInOrder) return null
    const suspensionsM = toJS(game.matchDls?.matchDlsSuspensions)
    const hasInvalidSuspension = find(suspensionsM, { oversRemaining: null })
    if (!hasInvalidSuspension) {
      let dlsValue = getDlsValue(
        par,
        target,
        {
          nb3: game.matchConfigs?.maxOvers,
          ng3: startingOvers2(),
          nb4: (game.getInningByNum(inningsInOrder[0].inningsMatchOrder)?.progressiveScores.runs || 0) - penaltyRuns2(),
          olft: par
            ? getOversRemaining({
                oversPerInnings: game.matchDls?.targetOvers || game.matchConfigs?.maxOvers || 0,
                oversBowled:
                  game.getInningByNum(inningsInOrder[1].inningsMatchOrder)?.progressiveScores.oversBowled || 0,
              })
            : undefined,
          wkt: par ? game.getInningByNum(inningsInOrder[1].inningsMatchOrder)?.progressiveScores.wickets : undefined,
          ni39: penaltyRuns2(),
        },
        suspensionsM?.map((s: IDLSSuspensionModel) => {
          return { ...s, inningsMatchOrder: s.inningsId === inningsInOrder[0].value ? 1 : 2 }
        })
      )
      if (target) {
        if (dlsValue !== (game.matchDls?.targetScore || 0) && !hasChangedTargetScore) setHasChangedTargetScore(true)
        game.updateDlsTargetScore(dlsValue)
        game.setDescription(MatchHelpers.gameDescriptionString(game))
      }
      if (par && (dlsValue < 0 || isNaN(dlsValue))) dlsValue = 0
      if (!ignoreReturn) return dlsValue
    } else {
      if (!ignoreReturn) return ''
    }
  }

  if (!isManual) calculateDlsTargetOvers()

  const dlsValueIssue = (): boolean => {
    if (!inningsInOrder) return false
    const suspensions = filter(game.matchDls?.matchDlsSuspensions, {
      inningsId: inningsInOrder[inningsInOrder.length - 1].value,
    })
    if (!suspensions) return false
    let existingValues: IDLSSuspensionModel = suspensions[0]
    let hasIssue = false
    suspensions.map((s: IDLSSuspensionModel) => {
      if (
        s.oversAt !== null &&
        s.runs !== null &&
        s.wickets !== null &&
        s.oversRemaining !== null &&
        existingValues.runs !== null &&
        existingValues.wickets !== null &&
        (Number(s.oversAt) < Number(existingValues.oversAt) ||
          Number(s.oversAt) > (game.matchConfigs?.maxOvers || 50) ||
          s.runs < existingValues.runs ||
          s.wickets < existingValues.wickets ||
          s.wickets > 10 ||
          Number(s.oversRemaining) > Number(existingValues.oversRemaining) ||
          Number(s.oversRemaining) > (game.matchConfigs?.maxOvers || 50))
      ) {
        hasIssue = true
      }
      existingValues = s
      return true
    })
    return hasIssue
  }

  // suspension data
  const data: IDLSSuspensionModel[] | null = useMemo(() => {
    return game.matchDls?.matchDlsSuspensions || null
  }, [game.matchDls, preManualDlsObject]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderFirstInningsTabPanel = (): ReactNode => {
    if (!inningsInOrder) return null

    const firstInnings = game.getInningByNum(inningsInOrder[0].inningsMatchOrder)
    const firstInningsOvers = String(firstInnings?.progressiveScores.oversBowled ?? '')
    const firstInningsRuns = Number(firstInnings?.progressiveScores.runs ?? 0)
    const firstInningsIsCompleted = firstInnings?.getInningStatus === 'COMPLETED'
    const firstInningsSuspensions = filter(data, { inningsId: inningsInOrder[0].value })
    const totalOvers1Display = String(totalOvers1() ?? '')
    const firstInningsOversLabel = firstInningsIsCompleted
      ? firstInningsOvers
      : `${firstInningsOvers} (${totalOvers1Display})`

    const oversRow: ReactNode = <DlsTextRow label="Overs:" value={firstInningsOversLabel} />

    const runsRow: ReactNode = <DlsTextRow label="Runs:" value={firstInningsRuns} />

    const manualTargetOversRow: ReactNode =
      isManual && inningsInOrder.length === 1 ? (
        <DlsEditableOversRow
          label="Target Overs:"
          testId="dls-overs1"
          value={Number(game.matchDls?.targetOvers || game.matchConfigs?.maxOvers || 20)}
          onChange={(value: string) => {
            if (
              (!isNil(game.matchDls?.targetOvers) && Number(value) !== game.matchDls?.targetOvers) ||
              (isNil(game.matchDls?.targetOvers) && Number(value) !== (game.matchConfigs?.maxOvers || 20))
            ) {
              updateTargetOversManual(Number(value))
            }
          }}
        />
      ) : null

    const suspensionPeriodsSection: ReactNode = !isManual ? (
      <DlsSuspensionSection
        canAddSuspension={!firstInningsIsCompleted}
        data={firstInningsSuspensions}
        game={game}
        inningsId={inningsInOrder[0].value}
        inSuperOver={inSuperOver}
        onAddSuspension={() => {
          addSuspension(inningsInOrder[0].value, 1)
        }}
      />
    ) : null

    return (
      <DlsTabPanelContainer dataJson={JSON.stringify(data)}>
        {oversRow}
        {runsRow}
        {manualTargetOversRow}
        {suspensionPeriodsSection}
      </DlsTabPanelContainer>
    )
  }

  const renderSecondInningsTabPanel = (): ReactNode => {
    if (!inningsInOrder || inningsInOrder.length <= 1) return null

    const secondInnings = game.getInningByNum(inningsInOrder[1].inningsMatchOrder)
    const secondInningsOvers = String(secondInnings?.progressiveScores.oversBowled ?? '')
    const secondInningsWickets = Number(secondInnings?.progressiveScores.wickets ?? 0)
    const displayedTargetOvers = parseOversValue(
      game.matchDls?.targetOvers || startingOvers2() || game.matchConfigs?.maxOvers
    )
    const manualTargetScore =
      game.matchDls?.targetScore ||
      (!isNil(game.getInningByNum(1)) ? (game.getInningByNum(1)?.progressiveScores.runs || 0) + 1 : '-')
    const displayedTargetScore = isChangingTargetScore ? '' : `${manualTargetScore}`
    const calculatedTargetScore = !isManual ? calculateDlsValue(false, true) || '-' : '-'
    const parScore = !isManual ? calculateDlsValue(true, false) || '0' : '0'
    const secondInningsSuspensions = filter(data, { inningsId: inningsInOrder[1].value })

    return (
      <DlsTabPanelContainer>
        <LooseFlex h="40px" align="center" direction="row">
          <LooseFlex flex={1}>
            <LooseText fontWeight="bold">{!isManual ? 'Starting Overs:' : 'Target Overs:'}</LooseText>
          </LooseFlex>
          <LooseFlex flex={1.75}>
            {isManual ? (
              <LooseBox w="50%">
                <LooseEditableField
                  key="overs2"
                  data-testid="dls-overs2"
                  height="32px"
                  width="100%"
                  textAlign="center"
                  value={game.matchDls?.targetOvers || game.matchConfigs?.maxOvers || 20}
                  onChange={(value: string) => updateTargetOversManual(Number(value))}
                  pattern="^\d{0,4}(?:\.[0-5]{1})?$"
                  errorMessage="Please enter numbers, or decimals up to 1 digit"
                />
              </LooseBox>
            ) : (
              <LooseText>{startingOvers2()}</LooseText>
            )}
          </LooseFlex>
        </LooseFlex>
        <LooseFlex h="40px" align="center" direction="row">
          <LooseFlex flex={1} alignItems={isManual ? 'center' : undefined}>
            <LooseText fontWeight="bold">Target Score:</LooseText>
          </LooseFlex>
          <LooseFlex flex={1.75} direction="row" alignItems="flex-start">
            {isManual ? (
              <>
                <LooseFlex flex={1}>
                  <LooseTextField
                    id="score2"
                    value={displayedTargetScore}
                    onChange={updateTargetScoreManual}
                    textAlign="center"
                    ignoreState
                    data-testid="manualUpdateTargetScoreTextField"
                  />
                </LooseFlex>
                <LooseFlex flex={1} justifyContent="flex-end">
                  {game.matchDls !== null && (
                    <LooseButton
                      onClick={() => {
                        if (!nonStandardGame) setIsManual(false)
                        clearTargetOversScoreManual()
                      }}
                      colorScheme="red"
                      data-testid="resetTargetOversScore"
                    >
                      Reset
                    </LooseButton>
                  )}
                </LooseFlex>
              </>
            ) : (
              <>
                <LooseText fontSize="xl" fontWeight="bold">
                  {calculatedTargetScore}
                </LooseText>
                <LooseText margin="1px 0 0 7px">{`(${displayedTargetOvers} overs)`}</LooseText>
              </>
            )}
          </LooseFlex>
        </LooseFlex>
        {!isManual && game.matchDls?.active && (
          <LooseFlex h="40px" align="center" direction="row">
            <LooseFlex flex={1}>
              <LooseText fontWeight="bold">Par Score:</LooseText>
            </LooseFlex>
            <LooseFlex flex={1.75}>
              {matchSettings?.scoreFormat === 'RUNS-WICKETS' && (
                <LooseText>{`${parScore}/${secondInningsWickets} (${secondInningsOvers} overs)`}</LooseText>
              )}
              {matchSettings?.scoreFormat === 'WICKETS-RUNS' && (
                <LooseText>{`${secondInningsWickets}/${parScore} (${secondInningsOvers} overs)`}</LooseText>
              )}
            </LooseFlex>
          </LooseFlex>
        )}
        {!isManual && (
          <DlsSuspensionSection
            canAddSuspension
            data={secondInningsSuspensions}
            game={game}
            inningsId={inningsInOrder[1].value}
            inSuperOver={inSuperOver}
            onAddSuspension={() => {
              addSuspension(inningsInOrder[1].value, 2)
            }}
          />
        )}
      </DlsTabPanelContainer>
    )
  }

  const renderTabs = (): ReactNode => {
    if (!inningsInOrder || !dlsOpen) return null

    return (
      <LooseTabs size="md" colorScheme="cls.blue" defaultIndex={inningsInOrder.length > 1 ? 1 : 0}>
        <LooseTabList borderColor="gray.200">
          {inningsInOrder.map((inning, idx) => (
            <LooseBox key={`innsTab${idx}`}>
              {!inning.superOver && <LooseTab>{`${inning.label} Innings`}</LooseTab>}
            </LooseBox>
          ))}
        </LooseTabList>
        <LooseTabPanels>
          {renderFirstInningsTabPanel()}
          {renderSecondInningsTabPanel()}
        </LooseTabPanels>
      </LooseTabs>
    )
  }

  const hasDlsIssue = dlsValueIssue()

  const renderModalFooter = (): ReactNode => {
    const showManualOverride = !nonStandardGame && !hasDlsIssue && (inningsInOrder?.length || 0) > 1

    return (
      <LooseModalFooter>
        {hasDlsIssue && (
          <LooseAlert status="error">
            <LooseAlertIcon />
            <LooseAlertDescription mr={2}>Suspensions are invalid or ordered incorrectly</LooseAlertDescription>
          </LooseAlert>
        )}
        {isManual && nonStandardGame && (
          <LooseAlert status="info">
            <LooseAlertIcon />
            <LooseAlertDescription mr={2}>Non-standard games can only use DLS manually</LooseAlertDescription>
          </LooseAlert>
        )}
        {showManualOverride && (
          <LooseFlex w="100%" justify="flex-start" align="center">
            <LooseCheckbox
              colorScheme="cls.yellow"
              isChecked={isManual}
              onChange={updateManualOverride}
              size="lg"
              data-testid="dlsIsManual"
            >
              Manual Override
            </LooseCheckbox>
          </LooseFlex>
        )}
        <LooseButton onClick={closeDls} size="md" colorScheme="cls.gray" ml={3} data-testid="doneDlSButton">
          Done
        </LooseButton>
      </LooseModalFooter>
    )
  }

  const renderDlsModal = (): ReactNode => {
    return (
      <LooseModal isOpen={dlsOpen} onClose={closeDls} size="lg" isCentered>
        <LooseModalOverlay />
        <LooseModalContent>
          <LooseModalHeader>Duckworth-Lewis-Stern</LooseModalHeader>
          <LooseModalCloseButton />
          <LooseModalBody>{renderTabs()}</LooseModalBody>
          {renderModalFooter()}
        </LooseModalContent>
      </LooseModal>
    )
  }

  const renderPowerPlayWarningDialog = (): ReactNode => {
    return (
      <LooseAlertDialog
        isOpen={powerPlayWarningOpen}
        leastDestructiveRef={cancelPowerPlayWarningRef}
        onClose={() => setPowerPlayWarningOpen(false)}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <LooseAlertDialogOverlay />
        <LooseAlertDialogContent>
          <LooseAlertDialogHeader fontSize="lg" fontWeight="bold">
            Power Play Information
          </LooseAlertDialogHeader>

          <LooseAlertDialogBody>
            Due to DLS, Power Plays will now need to be manually stopped and started for the remainder of the match
          </LooseAlertDialogBody>

          <LooseAlertDialogFooter>
            <LooseButton
              colorScheme="green"
              onClick={() => setPowerPlayWarningOpen(false)}
              ml={3}
              textTransform="capitalize"
              data-testid="powerPlayInfoDialogDone"
            >
              OK
            </LooseButton>
          </LooseAlertDialogFooter>
        </LooseAlertDialogContent>
      </LooseAlertDialog>
    )
  }

  return (
    <>
      {renderDlsModal()}
      {renderPowerPlayWarningDialog()}
    </>
  )
})
